import { TwAutocompleteOption } from '../../components/TwAutocomplete/TwAutocomplete';
import {
  aceShipmentTypesRaw,
  aciShipmentTypesRaw,
  countriesRaw,
  aciPackagingUnitsRaw,
  stateProvinceRaw,
  trailerTypesRaw,
  travelDocsRaw,
  truckTypesRaw,
  acePackagingUnitsRaw
} from './variables';
import { BcDocumentType, TravelDocument } from '../../redux/api/driverProfile/driverProfile.model';
import { TripStop } from '../../redux/api/trips/trips.model';
import { Order } from '../../redux/api/orders/orders.model';
import { MANIFEST_TYPE } from '../../redux/api/manifest/manifest.model';
import { portCodeOptions } from '../location/location';
import { LOCATION_TYPE } from '../../redux/api/load/load.model';
import { manifestTimeZonesRaw } from '../location/variables';

export const aciPackagingUnitOptions: TwAutocompleteOption[] = aciPackagingUnitsRaw.map((unit) => {
  return {
    id: unit.code,
    label: unit.name,
    value: unit.code
  };
});

export const acePackagingUnitOptions: TwAutocompleteOption[] = acePackagingUnitsRaw.map((unit) => {
  return {
    id: unit.code,
    label: unit.name,
    value: unit.code
  };
});

export const stateProvinceOptions = stateProvinceRaw.map((state) => {
  const label = `${state.name} (${state.code})`;
  return {
    id: state.code,
    label,
    value: state.name
  };
});

export const getTravelDocSummary = (
  travelDocs: TravelDocument[]
): { label: string; value: string }[] => {
  return [
    {
      label: 'Travel document 1',
      value: getTravelDocValue(travelDocs[0])
    },
    {
      label: 'Travel document 2',
      value: getTravelDocValue(travelDocs[1])
    },
    {
      label: 'Travel document 3',
      value: getTravelDocValue(travelDocs[2])
    }
  ];
};

const getTravelDocValue = (doc: TravelDocument): string => {
  if (!doc || !doc.type) return '-';
  
  const stateProvince = doc.state_province || '';
  const country = doc.country || '';
  
  let locationPart = '';
  if (stateProvince && country) {
    locationPart = `\n${stateProvince}, ${country}`;
  } else if (stateProvince) {
    locationPart = `\n${stateProvince}`;
  } else if (country) {
    locationPart = `\n${country}`;
  }
  
  return `${getTravelDocTypeLabel(doc.type)}: ${doc.number}${locationPart}`;
};

export const formatTravelDoc = (doc: TravelDocument): Record<string, string> | null => {
  const docOption = travelDocsRaw.find((td) => td.description === doc.type);
  const stateProvinceOption = stateProvinceRaw.find((state) => state.name === doc.state_province);
  const countryOption = countriesRaw.find((country) => country.name === doc.country);

  if (!docOption) return null;

  return {
    number: doc.number,
    type: docOption.code,
    ...(stateProvinceOption && { stateProvince: stateProvinceOption.code }),
    ...(countryOption && { country: countryOption.code })
  };
};

export const countriesOptions: TwAutocompleteOption[] = countriesRaw.map((country) => {
  const label = `${country.name} (${country.code})`;
  return {
    id: country.code,
    label,
    value: country.name
  };
});

export const getCountryLabel = (country: string | null | undefined): string | null => {
  if (!country) return null;
  const option = countriesOptions.find((c) => c.value === country);
  return option ? `${country} (${option?.id})` : null;
};

export const getStateProvinceLabel = (stateProvince: string | null | undefined): string | null => {
  if (!stateProvince) return null;
  const option = stateProvinceRaw.find((s) => s.name === stateProvince);
  return option ? `${stateProvince} (${option?.code})` : null;
};

export const getTravelDocTypeLabel = (type: string): string | null => {
  const option = travelDocsRaw.find((doc) => doc.description === type);
  return option ? `${type} (${option?.code})` : null;
};

export const truckTypeOptions: TwAutocompleteOption[] = truckTypesRaw.map((truck) => {
  return {
    id: truck.code,
    label: truck.name,
    value: truck.name
  };
});

export const trailerTypeOptions: TwAutocompleteOption[] = trailerTypesRaw.map((trailer) => {
  return {
    id: trailer.code,
    label: trailer.description,
    value: trailer.description
  };
});

export const aceShipmentTypeOptions: TwAutocompleteOption[] = aceShipmentTypesRaw.map(
  (shipment) => {
    return {
      id: shipment.code,
      label: shipment.description,
      value: shipment.description
    };
  }
);

export const aciShipmentTypeOptions: TwAutocompleteOption[] = aciShipmentTypesRaw.map(
  (shipment) => {
    return {
      id: shipment.code,
      label: shipment.description,
      value: shipment.description
    };
  }
);

export const provinceOfLoadingOptions: TwAutocompleteOption[] = stateProvinceRaw.map((province) => {
  return {
    id: province.code,
    label: province.name,
    value: province.name
  };
});

export const timezoneOptions: TwAutocompleteOption[] = manifestTimeZonesRaw.map((timezone) => {
  return {
    id: timezone.code,
    label: timezone.code,
    value: timezone.code
  };
});

export const getPortOption = (stop: TripStop | null): TwAutocompleteOption | null => {
  if (!stop) return null;
  return portCodeOptions().find((port) => port.id === stop.location?.port_code) ?? null;
};

export const getProvinceOption = (stateCode?: string): TwAutocompleteOption | null => {
  if (!stateCode) return null;
  return provinceOfLoadingOptions.find((province) => province.id === stateCode) ?? null;
};

export const getShipmentTypeOption = (manifestType: MANIFEST_TYPE): TwAutocompleteOption | null => {
  const options =
    manifestType === MANIFEST_TYPE.ACE ? aceShipmentTypeOptions : aciShipmentTypeOptions;
  const type = manifestType === MANIFEST_TYPE.ACE ? 'PAPS' : 'PARS';

  return options.find((option) => option.id === type) ?? null;
};

export const getManifestTripNumber = (
  manifestType: MANIFEST_TYPE,
  tripNumber: string | number,
  orders?: Order[]
): string => {
  if (!orders || !orders.length || !orders[0].division) return '';
  const { ca_carrier_code, scac } = orders[0].division;
  const prefix = manifestType === MANIFEST_TYPE.ACE ? scac : ca_carrier_code;

  return prefix ? prefix + tripNumber.toString().padStart(8, '0') : '';
};

export const getManifestControlNumber = (
  manifestType: MANIFEST_TYPE,
  shipmentType: string,
  sequence?: string | null,
  orders?: Order[]
): string => {
  if (!orders || !orders.length || !orders[0].division || !sequence) return '';
  const { ca_carrier_code, scac } = orders[0].division;
  const shipmentPrefix = shipmentType === 'PARS' || shipmentType === 'PAPS' ? shipmentType : '';
  const divPrefix = manifestType === MANIFEST_TYPE.ACE ? scac : ca_carrier_code;

  return divPrefix ? divPrefix + shipmentPrefix + sequence : '';
};

export const getDocumentTypeLabel = (type?: BcDocumentType): string => {
  if (!type) return '';
  return type === BcDocumentType.TRAVEL_DOCUMENTS ? 'Travel documents' : 'Fast card';
};

export const getTripPortsOfEntry = (
  stops: TripStop[]
): { usPort: TripStop | null; caPort: TripStop | null } => {
  const usPort = stops.find((stop) => stop.location?.type === LOCATION_TYPE.US_PORT) ?? null;
  const caPort = stops.find((stop) => stop.location?.type === LOCATION_TYPE.CA_PORT) ?? null;

  return {
    usPort,
    caPort
  };
};

export const validateScac = (code: string): boolean => {
  const regex = /^[A-Za-z]{4}$/; // 4 characters long and contains only letters
  return regex.test(code);
};

export const validateCCC = (code: string): boolean => {
  const regex = /^[0-9][A-Za-z0-9]{3}$/; // 4 characters long and starts with a number
  return regex.test(code);
};

/**
 * Clean string for BC api.
 * Allows alphanumeric characters (spaces, dashes, periods, commas, number signs, and single quotes are permitted)
 */

export const cleanAndSanitizeString = (str?: string): string => {
  if (!str) return '';
  // Remove control characters
  const cleaned = str.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');

  // Normalize to NFKD and strip accents (e.g., é -> e)
  const normalized = cleaned.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

  // Remove any characters not in the allowed set
  const sanitized = normalized.replace(/[^a-zA-Z0-9\s\-.,#']/g, '');

  // Normalize whitespace
  return sanitized.replace(/\s+/g, ' ').trim();
};
